/**
 * Utility functions for working with nested keys in translations
 */

/**
 * Gets a nested value from an object using dot notation
 * @param obj The object to traverse
 * @param path The path to the value using dot notation (e.g., "deals.card.soldBy")
 * @returns The value at the path, or undefined if not found
 */
export const getNestedValue = (
	obj: Record<string, unknown>,
	path: string
): string | undefined => {
	// If the key doesn't contain dots, use direct access
	if (!path.includes(".")) {
		return typeof obj[path] === "string" ? (obj[path] as string) : undefined;
	}

	// Handle nested path
	const keys = path.split(".");
	let current: Record<string, unknown> = obj;

	for (let i = 0; i < keys.length; i++) {
		const key = keys[i];

		// If we've reached a string value before the end of the path,
		// we can't traverse further
		if (typeof current !== "object" || current === null) {
			return undefined;
		}

		// If this is the last key and the value is a string, return it
		if (i === keys.length - 1 && typeof current[key] === "string") {
			return current[key] as string;
		}

		// Move to the next level
		current = current[key] as Record<string, unknown>;

		// If we hit undefined or null, stop traversing
		if (current === undefined || current === null) {
			return undefined;
		}
	}

	// If we've reached a leaf node and it's a string, return it
	return typeof current === "string" ? current : undefined;
};

/**
 * Determines if a translation key exists in a nested object
 * @param obj The object to check
 * @param path The path to check using dot notation
 * @returns True if the path exists and is a string
 */
export const hasNestedKey = (
	obj: Record<string, unknown>,
	path: string
): boolean => {
	return getNestedValue(obj, path) !== undefined;
};

/**
 * Converts a nested object to a flat one using dot notation
 * Used for backward compatibility with the existing system
 *
 * @param obj The nested object to flatten
 * @param prefix The prefix to use for keys
 * @returns A flattened object
 */
export const flattenTranslations = (
	obj: Record<string, unknown>,
	prefix = ""
): Record<string, string> => {
	return Object.keys(obj).reduce(
		(acc, key) => {
			const prefixedKey = prefix ? `${prefix}.${key}` : key;

			if (typeof obj[key] === "object" && obj[key] !== null) {
				Object.assign(
					acc,
					flattenTranslations(obj[key] as Record<string, unknown>, prefixedKey)
				);
			} else if (typeof obj[key] === "string") {
				acc[prefixedKey] = obj[key] as string;
			}

			return acc;
		},
		{} as Record<string, string>
	);
};
